import { Tooltip } from '@mui/material'
import type { DigitalTwinSetting } from 'api/digitalTwin/digitalTwin.types'
import { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'ui/atoms'
import TimeField from 'ui/components/CustomTable/components/TimeField/TimeField'
import Datetime from 'utils/datetime/datetime'
import PriorityTag from 'views/DigitalTwinCollectedSettingsView/components/PriorityTag'
import { getPriorityLevelDisplayNames } from 'views/DigitalTwinCollectedSettingsView/DigitalTwinCollectedSettingsView.helper'
import { getLabelForAttributeValue } from 'views/DigitalTwinSettingsView/DigitalTwinSettingsView.helper'
import type { EventPost } from 'views/EventsView/components/events.helper'
import { SettingEventLink, SettingManageButton } from './SettingActions'

const CARDS_PER_PAGE = 10

export type SettingCardOrder = { by: 'start_time' | 'updated_at'; descending: boolean }

/** A missing time counts as the earliest. */
function compareSettings(a: DigitalTwinSetting, b: DigitalTwinSetting, order: SettingCardOrder): number {
  const timeA = a[order.by]
  const timeB = b[order.by]
  const ascending = timeA === null ? (timeB === null ? 0 : -1) : timeB === null ? 1 : Datetime.compare(timeA, timeB)
  return order.descending ? -ascending : ascending
}

type SettingCardListProps = {
  settings: DigitalTwinSetting[]
  order: SettingCardOrder
  canEdit: boolean | undefined
  priorities: SystemSettingPriorityLevel[]
  operationalEvents?: Map<number, EventPost>
  showDeviationType: boolean
  onEditSetting: (setting: DigitalTwinSetting) => void
  onEventClick: (eventId: number) => void
}

/** One card per setting, ten at a time, for where a table's columns would be too narrow to read. */
export function SettingCardList({ settings, order, ...cardProps }: SettingCardListProps): ReactNode {
  const { t } = useTranslation()
  const [visibleCount, setVisibleCount] = useState(CARDS_PER_PAGE)

  if (settings.length === 0) {
    return <p className="py-3 text-grey-6">{t('No rows found')}</p>
  }

  const sortedSettings = [...settings].sort((a, b) => compareSettings(a, b, order))

  return (
    <div className="flex flex-col items-start">
      <ul className="w-full">
        {sortedSettings.slice(0, visibleCount).map((setting) => (
          <SettingCard key={setting.id} setting={setting} {...cardProps} />
        ))}
      </ul>
      {sortedSettings.length > visibleCount && (
        <Button link onClick={() => setVisibleCount((count) => count + CARDS_PER_PAGE)}>
          {t('Show more')}
        </Button>
      )}
    </div>
  )
}

type SettingCardProps = Omit<SettingCardListProps, 'settings' | 'order'> & { setting: DigitalTwinSetting }

function SettingCard({
  setting,
  canEdit,
  priorities,
  operationalEvents,
  showDeviationType,
  onEditSetting,
  onEventClick,
}: SettingCardProps): ReactNode {
  const { t } = useTranslation()
  const displayValue = setting.value !== null && setting.value !== undefined ? setting.value : setting.values
  const value = getLabelForAttributeValue(setting.attribute, displayValue) ?? ''
  const priorityLevel = priorities.find((priority) => priority.priority_level === setting.priority)
  const excludedFrom = setting.excluded_priority_layers?.length
    ? getPriorityLevelDisplayNames(setting.excluded_priority_layers, priorities)
    : ''
  const eventId = setting.operational_event

  return (
    <li className="flex items-start gap-4 border-0 border-b border-solid border-grey-3 py-3">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-semibold">{setting.unit ? `${value} ${setting.unit}` : value}</span>
          {showDeviationType && priorityLevel && <PriorityTag priority={priorityLevel} />}
        </div>
        <div>
          <span className="whitespace-nowrap">
            {setting.start_time === null ? '-' : <TimeField value={setting.start_time} />}
            {' –'}
          </span>{' '}
          <span className="whitespace-nowrap">
            <TimeField value={setting.end_time === null ? null : Datetime.ISODatetimeTo59Minutes(setting.end_time)} />
          </span>
        </div>
        {setting.comment && (
          <Tooltip title={setting.comment} arrow placement="top">
            <span className="truncate">{setting.comment}</span>
          </Tooltip>
        )}
        {showDeviationType && excludedFrom && (
          <div className="text-grey-6 text-xs">
            {t('Excluded from')}: {excludedFrom}
          </div>
        )}
      </div>
      <div className="flex shrink-0 flex-col items-end text-right text-grey-6 text-xs">
        <div className="mb-1 flex items-center gap-2">
          {eventId !== null && eventId !== undefined && (
            <SettingEventLink eventName={operationalEvents?.get(eventId)?.name} onClick={() => onEventClick(eventId)} />
          )}
          <SettingManageButton setting={setting} canEdit={canEdit} onEditSetting={onEditSetting} />
        </div>
        {setting.updated_at && (
          <>
            <span>{t('Last updated')}</span>
            <span className="whitespace-nowrap">
              <TimeField value={setting.updated_at} />
            </span>
            {setting.creator_name && <span>{setting.creator_name}</span>}
          </>
        )}
      </div>
    </li>
  )
}
